import React, { Suspense, memo, useMemo } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import routes from "../routes";
import { Loader2 } from "lucide-react";
import { RoleGuard } from "./RoleGuard";
import { useAuth } from "@/hooks/useAuth";

interface RouteItem {
  path: string;
  name: string;
  element?: React.ElementType;
  children?: RouteItem[];
  allowedRoles?: string[];
}

const AppContent = memo(() => {
  const { userDataLoading } = useAuth();

  // Memoizar la función de renderizado de rutas para evitar recálculos
  const renderRoutes = useMemo(() => {
    const render = (routes: RouteItem[]): React.ReactNode[] => {
      return routes.map((route: RouteItem, idx: number) => {
        if (route.element) {
          // Check if route has role restrictions
          const ElementComponent = route.element;
          const element = route.allowedRoles ? (
            <RoleGuard allowedRoles={route.allowedRoles}>
              <ElementComponent />
            </RoleGuard>
          ) : (
            <ElementComponent />
          );

          return (
            <Route key={idx} path={route.path} element={element}>
              {route.children && render(route.children)}
            </Route>
          );
        }
        return null;
      });
    };
    return render;
  }, []);

  const renderedRoutes = useMemo(() => renderRoutes(routes), [renderRoutes]);

  return (
    <div className="relative">
      {/* Loading overlay para userDataLoading - no afecta al sidebar */}
      {userDataLoading && (
        <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm text-gray-600">
              Cargando información del usuario...
            </p>
          </div>
        </div>
      )}

      <Suspense
        fallback={
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        }
      >
        <Routes>
          {renderedRoutes}
          <Route path="/login" element={<Navigate to="/" />} />
          <Route path="/welcome" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </div>
  );
});

AppContent.displayName = "AppContent";

export default AppContent;
